import { SocialConsentModal, SocialConsentModalProps } from '@minecraft.organisms';
import React, { useMemo, FC } from 'react';
import { Trans, useTranslation, TranslateFn } from '@blocs.i18n';
import { appConfig as globalAppConfig } from '@minecraft.app-config';

const PrivacyPolicyLink: FC = ({ children }) => (
  <a href="https://www.castingnetworks.com/privacy" target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

export const FEATURE_LIST_ITEMS = (): { key: string; content: React.ReactNode }[] => [
  {
    key: 'instagram-consent-feature1',
    content: <Trans i18nKey="common:profileEdit.social.instagram.consentModal.feature1" />,
  },
  {
    key: 'instagram-consent-feature2',
    content: <Trans i18nKey="common:profileEdit.social.instagram.consentModal.feature2" />,
  },
  {
    key: 'instagram-consent-feature3',
    content: <Trans i18nKey="common:profileEdit.social.instagram.consentModal.feature3" />,
  },
];

export const FOOTER_ITEMS = (t: TranslateFn): SocialConsentModalProps['footerItems'] => [
  {
    key: 'instagram-consent-footer-item1',
    icon: 'switch-accounts',
    title: t('common:profileEdit.social.consentModal.footer.itemHeader1'),
    description: t('common:profileEdit.social.consentModal.footer.itemDescription1'),
  },
  {
    key: 'instagram-consent-footer-item2',
    icon: 'lock',
    title: t('common:profileEdit.social.consentModal.footer.itemHeader2'),
    description: (
      <Trans
        i18nKey="common:profileEdit.social.consentModal.footer.itemDescription2"
        components={{
          a: <PrivacyPolicyLink />,
        }}
      />
    ),
  },
];

interface InstagramConsentModalProps {
  onConnect: () => void;
  mutationLoading: boolean;
  close: () => void;
}

export const InstagramConsentModal = ({ onConnect, mutationLoading, close }: InstagramConsentModalProps) => {
  const { t } = useTranslation();

  const { featureItems, footerItems } = useMemo(
    () => ({ featureItems: FEATURE_LIST_ITEMS(), footerItems: FOOTER_ITEMS(t) }),
    [t]
  );
  const modalButtons: SocialConsentModalProps['buttonsConfig'] = useMemo(
    () => [
      {
        label: t('common:button.notNow'),
        onClick: close,
        variant: 'secondary',
      },
      {
        label: t('common:profileEdit.social.consentModal.button.connectAndShare'),
        onClick: onConnect,
        disabled: mutationLoading,
      },
    ],
    [t, close, onConnect, mutationLoading]
  );

  return (
    <SocialConsentModal
      isOpen
      close={close}
      buttonsConfig={modalButtons}
      description={t('common:profileEdit.social.consentModal.description')}
      platformIcon="profile-social-instagram"
      previewImage={{
        src: `${globalAppConfig.cdnRoot}/images/static/profile-social-preview.png`,
        srcSet: `${globalAppConfig.cdnRoot}/images/static/profile-social-preview-4x.png`,
        alt: t('common:profileEdit.social.consentModal.previewImageAlt'),
      }}
      title={t('common:profileEdit.social.instagram.consentModal.title.premiumUser')}
      data-testid="instagram-auth-modal"
      footerItems={footerItems}
      featureHeader={t('common:profileEdit.social.consentModal.featureHeader.premiumUser')}
      features={featureItems}
    />
  );
};
